/**
 * @module app.cd
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { DrugService, IDrugSearchFilters } from 'app/cd/services/drug-service';
import { bindable } from 'aurelia-typed-observable-plugin';
import { Toaster } from 'app/shared/lib/toaster';
import { apiCall } from 'app/shared/lib/api/api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { IScrollTarget } from 'app/shared/attributes/ess-scroll-attribute';
import { fireEvents } from 'app/shared/lib/fire-events';
import * as CD from 'app/cd/models/essette-api';
import { I18N } from 'aurelia-i18n';
import { guid } from 'app/shared/lib/guid';


interface IDrugSelectEvent { drug: CD.Drug }
export type DrugSelectEvent = CustomEvent<IDrugSelectEvent>;


@autoinject
export class DrugSearch extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.object
    public searchFilters: IDrugSearchFilters = {};
    public tempSearchFilters: IDrugSearchFilters = {};

    protected searchName: string | null = null;
    protected searchIngredient: string | null = null;
    protected searchCode: string | null = null;
    protected searchTherapeuticClass: string | null = null;
    protected searchType: CD.DrugType | null = null;

    protected drugTypes: ReadonlyArray<CD.DrugType> | null = null;
    protected drugs: ReadonlyArray<CD.Drug> | null = null;

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 20;
    protected id: guid = guid();


    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $scroll!: IScrollTarget;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly $element: Element,
        private readonly service: DrugService,
        private readonly i18n: I18N,
        private readonly toaster: Toaster,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(bindingContext: Object, overrideContext: Object): void {
        this.loadDrugTypes();
    }


    ////****************** Events ***************************************************////


    ////****************** Functions ************************************************////
    public async search(searchFilters?: IDrugSearchFilters): Promise<void> {
        if(searchFilters) { this.searchFilters = searchFilters; }
        this.resultCount = 0;
        if(this.$pager) this.$pager.updatePage(0);
        await this.searchDrugs();
    }
    public async searchMedication(): Promise<void> {
        if(this.searchFilters) { this.searchFilters = { ...this.tempSearchFilters } }
        this.resultCount = 0;
        if(this.$pager) this.$pager.updatePage(0);
        await this.searchDrugs();
    }

    public reset(): void {
        this.searchFilters = {};
        this.tempSearchFilters = {};
        this.drugs = [];
        this.resultCount = 0;
        if(this.$pager) this.$pager.page = 0;
    }

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.searchDrugs();
    }

    protected selectDrug(drug: CD.Drug): void {
        if(drug && this.$element) {
            const detail: IDrugSelectEvent = { drug: drug };
            fireEvents(this.$element, ['item-edit', 'item-select'], detail);
        }

        this.reset();
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadDrugTypes(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            all: true
        };

        try {
            this.state.beginLoad();
            const response = await this.service.loadDrugTypes(options);
            this.drugTypes = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async searchDrugs(): Promise<void> {
        if(this.service.countFilters(this.searchFilters) === 0) {
            this.toaster.warn().message(this.i18n.tr('ui:messages.warnings.minimumFilterRequirements')).show();
            return;
        }

        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.sorting
        };
        const includes: string[] = ['category', 'dosageForm', 'activeIngredients', 'routes', 'therapeuticClasses'];

        try {
            this.state.beginLoad();
            const response = await this.service.searchDrugs(this.searchFilters, options, includes);
            this.drugs = response.results;
            this.resultCount = response.count || 0;
            if(this.resultCount === 1) {
                this.selectDrug(this.drugs[0]);
            } else {
                this.$scroll.scrollIntoView();
            }
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
